'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { ClipboardList, Plus, Search, Download, Eye, CheckCircle2, XCircle, AlertTriangle, ArrowLeft, X, Check,  } from 'lucide-react';
import {
  MOCK_REQUISITIONS, REQUISITION_STATUS_STYLES, PRIORITY_STYLES, PROCUREMENT_METHOD_LABELS,
  formatUGX, PurchaseRequisition, RequisitionStatus,
} from '../../components/procurementData';
import { useRole } from '@/lib/useRole';
import BudgetTracker, { BudgetSummaryPanel } from '../../components/BudgetTracker';

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: RequisitionStatus }) {
  const st = REQUISITION_STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
      {st.label}
    </span>
  );
}

// ─── Detail Drawer ────────────────────────────────────────────────────────────
function RequisitionDrawer({ req, onClose, onApprove, onReject }: {
  req: PurchaseRequisition;
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}) {
  const { can } = useRole();
  const canApprove = can('manage_procurement');
  const pri = PRIORITY_STYLES[req.priority];

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <div className="text-base font-bold text-gray-900">{req.requisitionNumber}</div>
            <div className="text-xs text-gray-400 mt-0.5">{req.farm} · {req.department}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-5">
          {/* Status & Priority */}
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={req.status} />
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${pri.bg} ${pri.text}`}>{pri.label} Priority</span>
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
              {PROCUREMENT_METHOD_LABELS[req.procurementMethod]}
            </span>
          </div>

          {/* Budget Tracker */}
          <BudgetTracker
            budgetCode={req.budgetCode}
            requestAmount={req.estimatedTotal}
            currency={req.currency}
            showSettings={canApprove}
          />

          {/* Details */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Requested By', value: req.requestedBy },
              { label: 'Request Date', value: req.requestDate },
              { label: 'Required By', value: req.requiredByDate },
              { label: 'Budget Code', value: req.budgetCode },
              { label: 'Cost Centre', value: req.costCentre },
              { label: 'Currency', value: req.currency },
            ].map(f => (
              <div key={f.label}>
                <div className="text-xs text-gray-400 font-medium">{f.label}</div>
                <div className="text-sm font-semibold text-gray-800 mt-0.5">{f.value}</div>
              </div>
            ))}
          </div>

          {/* Purpose */}
          <div>
            <div className="text-xs text-gray-400 font-medium mb-1">Purpose / Justification</div>
            <div className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3">{req.purpose}</div>
          </div>

          {/* Items */}
          <div>
            <div className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Requisition Items</div>
            <div className="space-y-2">
              {req.items.map(item => (
                <div key={item.id} className="bg-gray-50 rounded-xl p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-800">{item.description}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{item.category} · {item.unit}</div>
                      {item.specifications && <div className="text-xs text-gray-500 mt-1 italic">{item.specifications}</div>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-bold text-gray-900">{formatUGX(item.estimatedTotal)}</div>
                      <div className="text-xs text-gray-400">{item.quantity} × {formatUGX(item.estimatedUnitCost)}</div>
                    </div>
                  </div>
                  {item.currentStock !== undefined && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs">
                      <span className="text-gray-400">Current stock:</span>
                      <span className={`font-semibold ${item.currentStock < 20 ? 'text-red-600' : 'text-green-600'}`}>{item.currentStock} {item.unit}s</span>
                      {item.currentStock < 20 && <AlertTriangle size={11} className="text-red-500" />}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
              <span className="text-sm font-bold text-gray-700">Estimated Total</span>
              <span className="text-base font-bold text-gray-900">{formatUGX(req.estimatedTotal)}</span>
            </div>
          </div>

          {/* Approval Timeline */}
          {req.approvals.length > 0 && (
            <div>
              <div className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide">Approval Workflow</div>
              <div className="space-y-2">
                {req.approvals.map((ap, idx) => (
                  <div key={ap.id} className="flex items-start gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                      ap.action === 'approved' ? 'bg-green-100 text-green-700' :
                      ap.action === 'rejected' ? 'bg-red-100 text-red-700' :
                      ap.action === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {ap.action === 'approved' ? <Check size={12} /> : ap.action === 'rejected' ? <X size={12} /> : idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-800">{ap.approverName}</span>
                        <span className="text-xs text-gray-400">{ap.approverRole}</span>
                      </div>
                      {ap.action !== 'pending' && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {ap.action.charAt(0).toUpperCase() + ap.action.slice(1)} · {ap.date} {ap.time}
                        </div>
                      )}
                      {ap.action === 'pending' && <div className="text-xs text-amber-600 mt-0.5">Awaiting approval</div>}
                      {ap.comments && <div className="text-xs text-gray-500 mt-1 italic">"{ap.comments}"</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {canApprove && req.status === 'pending_approval' && (
          <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
            <button onClick={() => { onApprove(req.id); onClose(); }} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors">
              <CheckCircle2 size={15} /> Approve
            </button>
            <button onClick={() => { onReject(req.id); onClose(); }} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 transition-colors">
              <XCircle size={15} /> Reject
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function RequisitionsContent() {
  const { can } = useRole();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selected, setSelected] = useState<PurchaseRequisition | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [reqs, setReqs] = useState(MOCK_REQUISITIONS);

  const canCreate = can('manage_procurement');
  const canApprove = can('manage_procurement');

  const filtered = useMemo(() => {
    return reqs.filter(r => {
      const q = search.toLowerCase();
      const matchSearch = !q || r.requisitionNumber.toLowerCase().includes(q) || r.requestedBy.toLowerCase().includes(q) || r.department.toLowerCase().includes(q) || r.purpose.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || r.status === statusFilter;
      const matchPriority = priorityFilter === 'all' || r.priority === priorityFilter;
      return matchSearch && matchStatus && matchPriority;
    });
  }, [reqs, search, statusFilter, priorityFilter]);

  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  function handleApprove(id: string) {
    setReqs(prev => prev.map(r => r.id === id ? { ...r, status: 'approved' as RequisitionStatus } : r));
    showToast('Requisition approved successfully.');
  }

  function handleReject(id: string) {
    setReqs(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' as RequisitionStatus } : r));
    showToast('Requisition rejected.', 'error');
  }

  const stats = {
    total: reqs.length,
    pending: reqs.filter(r => r.status === 'pending_approval').length,
    approved: reqs.filter(r => r.status === 'approved' || r.status === 'completed').length,
    draft: reqs.filter(r => r.status === 'draft').length,
  };

  // Unique budget codes from all requisitions
  const allBudgetCodes = [...new Set(reqs.map(r => r.budgetCode))];

  return (
    <div className="px-4 md:px-6 xl:px-8 py-6 max-w-screen-2xl mx-auto">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm ${toast.type === 'success' ? 'bg-gray-900' : 'bg-red-700'}`}>
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${toast.type === 'success' ? 'bg-green-400' : 'bg-red-300'}`} />
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <Link href="/procurement" className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft size={16} className="text-gray-500" />
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">Purchase Requisitions</h1>
          <p className="text-xs text-gray-400 mt-0.5">Internal purchase requests — Idudi Poultry Farm</p>
        </div>
        {canCreate && (
          <Link href="/procurement/requisitions/new" className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors shadow-sm">
            <Plus size={14} /> New Requisition
          </Link>
        )}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-3 my-5">
        {[
          { label: 'Total', value: stats.total, color: 'text-gray-900' },
          { label: 'Pending Approval', value: stats.pending, color: 'text-amber-600' },
          { label: 'Approved', value: stats.approved, color: 'text-green-600' },
          { label: 'Draft', value: stats.draft, color: 'text-gray-400' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-3 text-center">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Budget Summary Panel */}
      <div className="mb-5">
        <BudgetSummaryPanel budgetCodes={allBudgetCodes} showSettings={canApprove} />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search requisitions..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white text-gray-700">
          <option value="all">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="pending_approval">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="converted_po">Converted to PO</option>
          <option value="completed">Completed</option>
        </select>
        <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white text-gray-700">
          <option value="all">All Priorities</option>
          <option value="emergency">Emergency</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>
        <button className="flex items-center gap-1.5 px-3 py-2.5 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-600">
          <Download size={14} /> Export
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Requisition #</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Department</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Requested By</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Priority</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Method</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Est. Total</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-gray-400">
                    <ClipboardList size={32} className="mx-auto mb-2 text-gray-200" />
                    No purchase requisitions found.
                  </td>
                </tr>
              ) : filtered.map(req => {
                const pri = PRIORITY_STYLES[req.priority];
                const st = REQUISITION_STATUS_STYLES[req.status];
                return (
                  <tr key={req.id} className="hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => setSelected(req)}>
                    <td className="px-4 py-3">
                      <div className="font-bold text-gray-900 text-xs">{req.requisitionNumber}</div>
                      <div className="text-xs text-gray-400">{req.items.length} item{req.items.length !== 1 ? 's' : ''}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">{req.department}</td>
                    <td className="px-4 py-3 text-xs text-gray-700">{req.requestedBy}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${pri.bg} ${pri.text}`}>{pri.label}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{PROCUREMENT_METHOD_LABELS[req.procurementMethod]}</td>
                    <td className="px-4 py-3 text-right text-xs font-bold text-gray-900">{formatUGX(req.estimatedTotal)}</td>
                    <td className="px-4 py-3"><StatusBadge status={req.status} /></td>
                    <td className="px-4 py-3 text-xs text-gray-400">{req.requestDate}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={e => { e.stopPropagation(); setSelected(req); }} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="View">
                          <Eye size={13} className="text-gray-500" />
                        </button>
                        {canApprove && req.status === 'pending_approval' && (
                          <>
                            <button onClick={e => { e.stopPropagation(); handleApprove(req.id); }} className="p-1.5 rounded-lg hover:bg-green-50 transition-colors" title="Approve">
                              <CheckCircle2 size={13} className="text-green-600" />
                            </button>
                            <button onClick={e => { e.stopPropagation(); handleReject(req.id); }} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Reject">
                              <XCircle size={13} className="text-red-500" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-400">{filtered.length} of {reqs.length} requisitions</span>
          <div className="flex items-center gap-1">
            <button className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40" disabled>Previous</button>
            <span className="px-3 py-1.5 text-xs bg-orange-600 text-white rounded-lg font-semibold">1</span>
            <button className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40" disabled>Next</button>
          </div>
        </div>
      </div>

      {/* Drawer */}
      {selected && (
        <RequisitionDrawer
          req={selected}
          onClose={() => setSelected(null)}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}
    </div>
  );
}
